import React, { createContext, useContext, useMemo, useState } from 'react';
import { Locale, TranslationKey, translations } from './translations';

const STORAGE_KEY = 'stock-checker-locale';

function initialLocale(): Locale {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'de' || stored === 'en') return stored;
  return 'en';
}

interface LocaleValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, values?: Record<string, string>) => string;
}

const LocaleContext = createContext<LocaleValue | null>(null);

export function LocaleProvider({ children }: React.PropsWithChildren) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const value = useMemo<LocaleValue>(
    () => ({
      locale,
      setLocale: (next) => {
        window.localStorage.setItem(STORAGE_KEY, next);
        setLocaleState(next);
      },
      t: (key, values = {}) => {
        const [section, name] = key.split('.') as [keyof typeof translations.de, string];
        let result = (translations[locale][section] as Record<string, string>)[name] ?? key;
        Object.entries(values).forEach(([name, value]) => {
          result = result.replace(`{{${name}}}`, value);
        });
        return result;
      },
    }),
    [locale],
  );
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale must be used within LocaleProvider');
  return value;
}
